<template>
  <div>
    <v-card style="padding-bottom: 20px" tile>
      <v-card-actions class="pl-5 py-3 d-flex justify-space-between">
        <div class="d-flex align-center">
          <span class="pr-2">
            <v-btn style="text-decoration:none !important;" :disabled="selected.length <= 0" @click="showSelected=true"
                small
                outlined 
                color="primary">View Selected</v-btn>
          </span>
          <span>
            <cluster-select @clearSelections="selected = [], selectAll=false, selectedEntities = []" :disabled="notSelected" :entityType="'publisher'" :entityIDType="'publisher_id'" :entities="selected" :entity-labels="clusterEntityLabels" />
          </span>
        </div>
        <div class="d-flex align-center pr-2 pb-5 pt-4">
          <span class="mr-4"> 
          <v-btn-toggle v-model="viewMode" mandatory class="view-toggle" @change="saveViewMode" color="primary">
            <v-btn small value="list">
              LIST VIEW
            </v-btn>
            <v-btn small value="card">
              CARD VIEW
            </v-btn>
          </v-btn-toggle>
          </span>
        </div>
      </v-card-actions>
      
      <!-- Card View -->
      <div v-if="viewMode === 'card'" class="card-view-container">
        <v-skeleton-loader
          v-if="isLoading"
          type="card"
          class="mb-4"
        ></v-skeleton-loader>
        <v-row v-else>
          <v-col
            v-for="item in entities"
            :key="item.id"
            cols="12"
            md="4"
            sm="6"
          >
            <v-card class="publisher-card" elevation="2" @click="entity_selected(item.id)" style="cursor: pointer; height: 100%;">
              <v-card-text class="pb-2">
                <div class="d-flex justify-space-between mb-2 card-header-row">
                  <div class="d-flex align-center" style="flex: 1; min-width: 0;">
                    <v-avatar :color="govAvatarColor(item.gov_type)" size="36" class="mr-3 flex-shrink-0">
                      <span class="white--text caption">{{ item.gov_type }}</span>
                    </v-avatar>
                    <div class="flex-grow-1" style="min-width: 0;">
                      <div class="d-flex align-center">
                        <h3 class="subtitle-1 font-weight-bold mb-0" style="line-height: 1.2;">
                          <a 
                            :href="$router.resolve({ name: 'entity', params: { id: item.id } }).href"
                            @click.stop
                            target="_blank"
                            class="text-decoration-none"
                          >
                            {{ item.name }}
                          </a>
                        </h3>
                      </div>
                      <!-- COFOG Classifications or Industries -->
                      <div v-if="item.metadata && item.metadata.cofog_classifications && item.metadata.cofog_classifications.length > 0" class="mt-1">
                        <span v-for="(cofog, idx) in item.metadata.cofog_classifications.slice(0, 3)" :key="idx">
                          <v-chip
                            class="my-1 ml-0 mr-2"
                            color="secondary"
                            label
                            text-color="secondary"
                            small
                            outlined
                          >
                            <v-icon small left>mdi-label</v-icon>
                            {{ cofog.name }}
                          </v-chip>
                        </span>
                      </div>
                      <div v-else-if="item.industry" class="mt-1">
                        <span v-for="(ind, idx) in industries(item).slice(0, 3)" :key="idx">
                          <v-chip
                            class="my-1 ml-0 mr-2"
                            color="secondary"
                            label
                            text-color="secondary"
                            small
                            outlined
                          >
                            <v-icon small left>mdi-label</v-icon>
                            {{ ind }}
                          </v-chip>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="d-flex align-center flex-shrink-0 ml-2 card-actions-row">
                    <v-checkbox
                      @click.stop
                      v-model="selected"
                      :value="item.id"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </div>
                </div>

                <!-- Description -->
                <div v-if="item.metadata && item.metadata.description" class="body-2 mb-3 text--secondary" style="line-height: 1.5;">
                  {{ truncateDescription(item.metadata.description, 150) }}
                </div>

                <!-- Financial Data -->
                <div class="financial-data mt-3 mb-2">
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span class="caption grey--text">Awarded (All Time)</span>
                    <span class="body-2 font-weight-medium">
                      {{ formattedCurrency(item.award_total) }}
                    </span>
                  </div>
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span class="caption grey--text">Contracts</span>
                    <span class="body-2 font-weight-medium">
                      {{ numFormatter(item.num_notices) }}
                    </span>
                  </div>
                </div>

                <!-- Actions -->
                <div class="d-flex justify-space-between mt-3">
                  <v-tooltip top dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <v-btn 
                        small 
                        color="#4EB888" 
                        light 
                        outlined
                        :href="$router.resolve({ name: 'entity', params: { id: item.id } }).href"
                        target="_blank"
                        @click.stop
                        v-on="on"
                      >
                        <v-icon small left>mdi-open-in-new</v-icon>
                        View Profile
                      </v-btn>
                    </template>
                    <span>Open full record</span>
                  </v-tooltip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        
        <!-- Pagination for Card View -->
        <div class="text-center mt-4" v-if="num_pages > 1">
          <v-pagination 
            v-model="page_num" 
            :length="num_pages" 
            color="#4EB888" 
            total-visible="5"
            prev-icon="mdi-menu-left" 
            next-icon="mdi-menu-right"
            @input="handlePageChange"
          ></v-pagination>
        </div>
      </div>

      <!-- List View -->
      <div v-else>
      <v-simple-table style="width:100%;">
        <thead>
          <tr>
            <th class="pl-5">
              <v-checkbox v-model="selectAll" @click="user_clicked_select_all(), select_all()"></v-checkbox>
            </th>
            <th class="text-left subtitle-2 font-weight-bold py-3 pl-5 pr-1">
              Gov
            </th>
            <th
              class="text-left subtitle-2 font-weight-bold py-3 pl-1 pr-2"
            >
            Entity
            </th>
            <th
              class="text-center subtitle-2 font-weight-bold py-3 p2-2 pl-1 ml-1"
            >
              <div>Awarded</div> 
              <div class="caption" style="font-weight:normal !important;">All Time</div>
            </th>
             <th
              class="text-center subtitle-2 font-weight-bold py-3 p2-2 pl-1 ml-1"
            >
              <div>Contracts</div> 
              <div class="caption" style="font-weight:normal !important;">All Time</div>
            </th>
            <th
              class="text-center subtitle-2 font-weight-bold py-3 p2-2 pl-1 ml-1"
            >
              <div>Awarded</div> 
              <div class="caption" style="font-weight:normal !important;">Last 12 Months</div>
            </th>
            <th
              class="text-center subtitle-2 font-weight-bold py-3 p2-2 pl-1 ml-1"
            >
             <div>Avg Contract</div> 
              <div class="caption" style="font-weight:normal !important;">Last 12 Months</div>
            </th>
            <th
              class="text-center subtitle-2 font-weight-bold py-3 p2-2 pl-1 ml-1"
            >
              <div>New Contracts</div> 
              <div class="caption" style="font-weight:normal !important;">Last 12 Months</div>
            </th>
            <th
              class="text-center subtitle-2 font-weight-bold py-3 p2-2 pl-1 ml-1"
            >
              <div>Expiring Contracts</div> 
              <div class="caption" style="font-weight:normal !important;">Next 12 Months</div>
            </th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr>
            <td colspan="9">
              <v-progress-linear v-show="isLoading" slot="progress" color="primary" indeterminate width="100%"></v-progress-linear>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-for="entity in entities" :key="entity.id">
            <td class="py-0 pl-5 pr-1 ml-1">
              <v-checkbox
                @click="select(), updateSelectedEntities()"
                v-model="selected"
                :value="entity.id"
              ></v-checkbox>
            </td>
            <td class="py-2 pl-5 pr-0 mr-1">
              <v-avatar
                :color="govAvatarColor(entity.gov_type)"
                size="36"
                class="mr-2"
              >
                <span class="white--text caption">{{ entity.gov_type }}</span>
              </v-avatar>
            </td>
            <td class="py-2 px-1 ml-1" style="max-width:300px; min-width:120px;">
              <div
                class="subtitle-2 d-inline-block text-truncate"
                style="max-width:350px; min-width:120px;"
              >
                <a 
                  :href="$router.resolve({ name: 'entity', params: { id: entity.id } }).href"
                  target="_blank"
                  class="text-decoration-none"
                >{{entity.name}}</a>
              </div>
              <div
                  class="caption pt-0 mt-0"
                >
                <!-- <span v-if="!entity.clusters"> -->
                  <span v-for="e in industries(entity)" :key="e">
                    <v-chip v-if="entity.industry"
                      class="my-1 ml-0 mr-2"
                      color="secondary"
                      label
                      text-color="secondary"
                      small
                      outlined
                    >
                    <v-icon small left>
                      mdi-label
                    </v-icon>
                    {{e}}
                    </v-chip>
                  </span>
                <!-- </span> -->
                  <span v-for="clstr in entity.clusters" :key="clstr.id">
                    <router-link
                            :to="{ name: 'cluster', params: { clusterId: clstr.id } }"
                          >
                      <v-chip v-if="clstr"
                        class="my-1 ml-0 mr-2"
                        color="primary"
                        label
                        text-color="primary"
                        small
                        outlined
                      >
                      <v-icon small left>
                        mdi-hexagon-multiple
                      </v-icon>
                      {{clstr.name}}
                      </v-chip>
                    </router-link>
                  </span>
                </div>
            </td>

            <td class="py-2 px-1 text-center">
              <router-link
                  :to="{ name: 'search', query: { publisher_id: entity.id, supplier_id: supplier_id } }"
                >
              {{ formattedCurrency(entity.award_total) }}
              </router-link>
            </td>
            <td class="py-2 px-1 text-center">
              <router-link
                  :to="{ name: 'search', query: { publisher_id: entity.id, supplier_id: supplier_id } }"
                >
                {{ numFormatter(entity.num_notices) }}
              </router-link>
            </td>
            <td class="py-2 px-1 text-center">
              <span v-if="entity.growth && entity.growth.current_year_total">
                {{ formattedCurrency(entity.growth.current_year_total) }}
              </span>
              <span v-else>—</span>
              <span v-if="entity.growth && entity.growth.spend_change_percent">
                    <v-icon class="py-0 pr-0 mr-0" :color="growth_format(entity.growth.spend_change_percent).color">
                      {{growth_format(entity.growth.spend_change_percent).icontype}}
                    </v-icon>
                    {{ growth_format(entity.growth.spend_change_percent).change }}%
                  </span>
            </td>
            <td class="py-2 px-1 text-center">
              <span v-if="entity.growth && entity.growth.curr_yr_avg_contract">
                {{ formattedCurrency(entity.growth.curr_yr_avg_contract) }}
              </span>
              <span v-else>—</span>
              <span v-if="entity.growth && entity.growth.avg_change_percent">
                    <v-icon class="py-0 pr-0 mr-0" :color="growth_format(entity.growth.avg_change_percent).color">
                      {{growth_format(entity.growth.avg_change_percent).icontype}}
                    </v-icon>
                    {{ growth_format(entity.growth.avg_change_percent).change }}%
                  </span>
            </td>
          
            <td class="py-2 px-1 text-center">
              <span v-if="entity.growth && entity.growth.current_year_vol">
                {{ numFormatter(entity.growth.current_year_vol) }}
              </span>
              <span v-else>—</span>
              <span v-if="entity.growth && entity.growth.vol_change_percent">
                    <v-icon class="py-0 mr-0 pr-0" :color="growth_format(entity.growth.vol_change_percent).color">
                      {{growth_format(entity.growth.vol_change_percent).icontype}}
                    </v-icon>
                    {{ growth_format(entity.growth.vol_change_percent).change }}%
                  </span>
            </td>

            <td class="py-2 px-1 text-center">
              <span v-if="entity.expiring_soon && entity.expiring_soon.next_year_total">
                {{ formattedCurrency(entity.expiring_soon.next_year_total) }}
              </span>
              <span v-else>—</span>
              <span v-if="entity.expiring_soon && entity.expiring_soon.spend_change_percent">
                <v-icon class="py-0 mr-0 pr-0" :color="growth_format(entity.expiring_soon.spend_change_percent).color">
                  {{growth_format(entity.expiring_soon.spend_change_percent).icontype}}
                </v-icon>
                {{ growth_format(entity.expiring_soon.spend_change_percent).change }}%
              </span>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

        <div class="text-center" style="max-width:800px">
          <v-pagination
            v-if="entities.length > 1"
            v-model="page_num"
            color="#4EB888"
            :length="num_pages"
            total-visible="5"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
            @input="handlePageChange"
          ></v-pagination>
        </div>
      </div>
    </v-card>

    <v-dialog v-model="showSelected" width="50%">
      <v-card>
        <v-list
        
        flat
      >
      <span><v-subheader>Currently selected entities</v-subheader></span><span> 
            <v-btn class="caption ml-2" x-small text color="primary" 
                  :disabled="selected.length <= 0" 
                  @click="(selectAll = false), user_clicked_select_all(), showSelected=false">
              Clear All
            </v-btn></span>
      <v-btn 
        absolute
        top
        right
        icon
        @click="showSelected = false"
        medium
      >
        <v-icon medium>
          mdi-close
        </v-icon>
      </v-btn>

      <v-card flat class="px-2 py-2 mx-2 my-2" min-height="300" max-height="420" style="flex-grow: 1 !important; overflow:auto !important;">

          <v-row
            align="center"
            justify="start"
          >
            <v-col
              v-for="entity in selectedEntities"
              :key="entity.id"
              class="py-1"
            >
              <v-chip
                color="primary"
                small
                label
                clearable
                dense
                close
                dark
                @click:close="deselectEntity(entity.id)"
              >
                {{ entity.name }}
              </v-chip>
            </v-col>
          </v-row>
      </v-card>
      
    </v-list>
    </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { bus } from "@/main.js";
import { fetchPublishers, fetchPublisherDirectory } from "../APIManager";
import { growth_format, formattedCurrency, numFormatter, govColor } from "../Utils.js";
import ClusterSelect from "../ClusterSelect.vue";

export default {
  components: {
    ClusterSelect
  },

  props: {
    supplier_id: Number,
    useDirectoryAPI: {
      type: Boolean,
      default: true  // Default to using new directory API
    }
  },
  data() {
    return {
      showSelected: false,
      saveButtonActive: false,
      clusterbox: false,
      selectAll: false,
      selected: [],
      selectedCount: 0,
      item: 0,
      entities: [],
      entities_temp: [],
      page_num: 1,
      num_pages: 5,
      isLoading: true,
      userClickedSelectAll: false,
      entitiesViewedCount: 0,
      selectedEntities: [],
      viewMode: 'list' // 'list' or 'card'
      //current_selection:
    };
  },

  mounted() {
    // Load view mode from localStorage
    const savedViewMode = localStorage.getItem('entityViewMode');
    if (savedViewMode === 'list' || savedViewMode === 'card') {
      this.viewMode = savedViewMode;
    }
    
    this.buildQuery();

    bus.$on("search-cleared", value => {
      this.entities = this.entities_temp;
    });
  },

  methods: {

    growth_format,
    numFormatter,

    select_all(){
      if (this.selectAll) {
          for (let entity in this.entities) {
            //////////////console.log (this.entities);
            //check the entity id is not already in the selected entities
            let index = this.selected.findIndex(x => x === this.entities[entity].id);
            //////////////console.log ("INDEX= ", index, " || ENTITY ID: ", this.entities[entity].id);
            if (index < 0){
              this.selected.push(this.entities[entity].id);
              this.updateSelectedEntities();
            }
            
          }
      }
    },

    user_clicked_select_all(){
      this.userClickedSelectAll = this.selectAll;
      if (!this.selectAll){ //if the user has physically unclicked selectall
        this.selected = [];
        this.selectedEntities = [];
      }
    },
    
    select() {
      ////////////console.log ("selected :", this.selected);
        this.selectAll = false;
    },

    updateSelectedEntities(){

      for (let s in this.selected) { 
        //check the entity id is not already in the selected entities
        let entity = this.entities.find(x => x.id === this.selected[s]);
        ////////////console.log ("updateSelectedEntities :", entity);

        if (entity){
          let index = this.selectedEntities.findIndex(x => x.id === entity.id);
          if (index < 0) {
            this.selectedEntities.push(entity);
          }

        }
      }
    },

    deselectEntity(id){

      let index = this.selected.findIndex(x => x === id);
      //////////////console.log (index,  this.entities[entity].id);
      if (index >= 0) { // only splice array when item is found
        this.selected.splice(index, 1); // 2nd parameter means remove one item only
      }

      let entityIndex = this.selectedEntities.findIndex(x => x.id === id);

      if (entityIndex >= 0) { // only splice array when item is found
        this.selectedEntities.splice(entityIndex, 1); // 2nd parameter means remove one item only
      }

      this.selectAll = false;

    },

    industries(entity){
      var ind = entity["industry"];
      var industries = []
      if (ind && ind.includes(","))
        industries = ind.split(',');
      else
        industries[0] = ind;

      return industries;
    },

    buildQuery(){
      if (this.useDirectoryAPI) {
        if (this.$route.query["page"]) {
          this.page_num = parseInt(this.$route.query["page"]);
          this.loadPublisherDirectory(this.page_num, this.$route.query);
        } else {
          this.page_num = 1;
          this.loadPublisherDirectory(this.page_num, this.$route.query);
        }
      } else {
        // Legacy: use old fetchPublishers
        if (this.supplier_id){
          var query = {page: this.page_num, supplier_id: this.supplier_id}
          this.loadPublishers(this.page_num, query);
        }
        else if (this.$route.query["page"]){
          this.page_num = this.$route.query["page"]
          this.loadPublishers(this.page_num, this.$route.query);
        }
        else{
          this.page_num = 1;
          this.loadPublishers(this.page_num, this.$route.query);
        }
      }
    },

    formattedCurrency,

    entity_selected(entity_id) {
      const route = this.$router.resolve({ name: "entity", params: { id: entity_id } });
      window.open(route.href, '_blank');
    },
    
    loadPublisherDirectory(page_num, query) {
      this.isLoading = true;
      fetchPublisherDirectory(page_num, query).then(results => {
        this.entities = results.data || [];
        this.num_pages = results.stats.total_pages || 1;
        this.page_num = results.stats.page_no || page_num;
        this.select_all();
        this.isLoading = false;
      }).catch(error => {
        console.error("Error loading publisher directory:", error);
        this.isLoading = false;
      });
    },
    
    handlePageChange(page) {
      // Update route with new page
      const query = { ...this.$route.query, page: page };
      this.$router.push({ query: query }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err);
        }
      });
    },
    
    truncateDescription(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    
    saveViewMode() {
      localStorage.setItem('entityViewMode', this.viewMode);
    },

    loadPublishers(page_num, query) {

      ////////////////console.log("QUERY FROM SUPP PROFILE: ", query);
      this.isLoading = true;

      //var qry = JSON.parse(JSON.stringify(query));

      fetchPublishers(page_num, query).then(results => {
        this.entities = results.data;
        
        this.num_pages = results.stats.total_pages;
        this.page_num = results.stats.page_no;
        this.select_all();
        this.isLoading = false;

        ////////////////console.log("loadPublishers data: ", results);
      });
    },

    disableIcon(url) {
      if (!url) {
        return true;
      } else {
        return false;
      }
    },

    govAvatarColor(gov) {
      return govColor(gov);  
    }
  },
  created() {
    //this.loadPublishers(this.page_num);
  },

  computed: {
    notSelected(){
      if (this.selected.length > 0) return false;
      else return true;
    },
    clusterEntityLabels() {
      const labels = {};
      for (const entity of this.selectedEntities) {
        if (entity?.id != null && entity?.name) {
          labels[entity.id] = entity.name;
        }
      }
      return labels;
    }
  },
  watch: {
     $route(to, from) {
      if (this.useDirectoryAPI || (to !== from)) {
        this.buildQuery();
      }
    },
  }
};
</script>
<style>
.wrap-text {
  word-wrap: normal !important;
}

.chip-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}
.nav-list {
  background-color: "#303030";
  border-bottom: 2pt white;
}
.list-item {
  text-decoration: none !important;
  color: "#FFFFFF" !important;
}

.bottom-border {
  border-bottom: 2pt white;
}

/* Card View Styles */
.publisher-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.publisher-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.financial-data {
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  padding: 12px 0;
}

.view-toggle .v-btn {
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.view-toggle .v-btn--active {
  background-color: var(--v-primary-base, #1976d2) !important;
  color: white !important;
}

.card-header-row {
  align-items: flex-start;
}

.card-header-row .card-actions-row {
  align-items: center;
  align-self: flex-start;
  margin-top: 0;
}

.card-header-row h3 {
  display: inline-block;
  vertical-align: middle;
}

.card-actions-row {
  align-items: center;
}

@media (max-width: 960px) {
  .card-header-row {
    align-items: flex-start;
  }
  
  .card-header-row .card-actions-row {
    align-items: center;
    margin-top: 0;
    padding-top: 0;
  }
}
</style>
